// Engagement letter layout — THE single source of the letter's legal text.
//
// Imported by both the CRA app (src/engagementPDF.js) and the Node MCP server
// (mcp-server/src/engagement/render.js). There is deliberately only one copy:
// two copies of ~40 paragraphs of boilerplate would drift, and two clients
// could end up signing materially different terms.
//
// This module is environment-agnostic — no window, no document. The caller
// supplies the jsPDF constructor and decides how to get bytes out.
//
// .mjs is load-bearing: this file sits under the CRA tree (whose package.json
// has no "type":"module"), so Node would otherwise parse it as CommonJS and
// choke on `export`. The extension forces ESM in both toolchains.

import { LOGO_B64_DATA } from "./engagementAssets.mjs";

/** Bump deliberately when the legal text changes. */
export const ENGAGEMENT_TEMPLATE_VERSION = "2025.1";

/**
 * Build the engagement letter. Returns the jsPDF document; the caller extracts
 * bytes (browser: output("blob"); Node: output("arraybuffer")).
 *
 * @param {Function} jsPDFCtor            - the jsPDF constructor
 * @param {object}   client               - client record
 * @param {object}   [opts.staffSignatures] - { Bibek|Biraj|Ben: dataUrl }
 */
export function buildEngagementLetterDoc(jsPDFCtor, client, opts = {}) {
  const staffSignatures = opts.staffSignatures || {};
  const doc = new jsPDFCtor({ unit: "pt", format: "letter" });

  const W = 612;
  const marginL = 72;
  const marginR = 72;
  const contentW = W - marginL - marginR;
  let y = 0;

  const setFont = (style = "normal", size = 10) => {
    doc.setFont("times", style);
    doc.setFontSize(size);
  };

  const text = (str, x, yPos, opts = {}) => doc.text(str, x, yPos, opts);

  // Page break threshold — text must stay above this Y
  const PAGE_BOTTOM = 700;
  const LINE_H = (size) => size * 1.4;
  let currentPage = 1;
  const TOTAL_PAGES = 7;
  let justStartedPage = true; // track if we're at the top of a fresh page

  // Auto-paginating line writer — ensures space before each line
  const ensureSpace = (size = 10) => {
    if (y + LINE_H(size) > PAGE_BOTTOM) newPage();
  };

  // Write wrapped text, paginating mid-paragraph as needed
  const para = (str, opts = {}) => {
    const { indent = 0, bold = false, size = 10 } = opts;
    setFont(bold ? "bold" : "normal", size);
    const lines = doc.splitTextToSize(str, contentW - indent);
    lines.forEach((line) => {
      ensureSpace(size);
      doc.text(line, marginL + indent, y);
      y += LINE_H(size);
    });
  };

  // Write a mixed bold+normal line, paginating line by line
  const mixedLine = (parts, size = 10) => {
    const boldPart = parts.find(p => p.bold);
    const boldText = boldPart ? boldPart.txt : "";
    const restText = parts.filter(p => !p.bold).map(p => p.txt).join("");
    const fullText = boldText + restText;
    setFont("normal", size);
    const allLines = doc.splitTextToSize(fullText, contentW);
    allLines.forEach((line, i) => {
      ensureSpace(size);
      if (i === 0 && boldText) {
        setFont("bold", size);
        doc.text(boldText, marginL, y);
        const bw = doc.getTextWidth(boldText);
        const lineRest = line.substring(boldText.length);
        if (lineRest) { setFont("normal", size); doc.text(lineRest, marginL + bw, y); }
      } else {
        setFont("normal", size);
        doc.text(line, marginL, y);
      }
      y += LINE_H(size);
    });
  };

  const hRule = () => {
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.5);
    doc.line(marginL, y, W - marginR, y);
    doc.setDrawColor(0, 0, 0);
    y += 8;
  };

  // Draw real logo image
  const drawLogo = (yPos) => {
    try {
      // Logo image: actual 2000x1312px, ratio 1.524 → 120pt wide = 78.7pt tall
      doc.addImage(LOGO_B64_DATA, "JPEG", marginL, yPos, 120, 79);
    } catch (e) {
      // Fallback to text logo if image fails
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(41, 171, 226);
      doc.text("Net", marginL, yPos + 24);
      const nw = doc.getTextWidth("Net");
      doc.setTextColor(141, 198, 63);
      doc.text("Save", marginL + nw, yPos + 24);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      doc.text("TAX & ACCOUNTING", marginL, yPos + 34);
      doc.setTextColor(0, 0, 0);
    }
    return yPos + 86;
  };

  const drawFooter = () => {
    const footerY = 736;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.4);
    doc.line(marginL, footerY - 6, W - marginR, footerY - 6);
    doc.setFontSize(8);
    doc.setFont("times", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text("Tel: (619) 878-1514", marginL, footerY + 4);
    doc.text("Email: team@netsavetax.com", marginL, footerY + 14);
    doc.text("Web: www.netsavetax.com", marginL, footerY + 24);
    doc.text(`Page ${currentPage} of ${TOTAL_PAGES}`, W - marginR, footerY + 14, { align: "right" });
    // Always fully restore body font state after footer
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont("times", "normal");
    doc.setLineWidth(0.5);
  };

  const drawHeader = () => {
    y = 36;
    y = drawLogo(y);
    y += 6;
    // Guarantee body font state after logo (which may switch to helvetica fallback)
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    justStartedPage = false; // content started
  };

  const newPage = () => {
    drawFooter();
    doc.addPage();
    currentPage++;
    drawHeader();
    justStartedPage = true;
  };
  const forcedPageBreak = () => {
    // Only break if we're not already at the top of a fresh page
    if (!justStartedPage) {
      drawFooter();
      doc.addPage();
      currentPage++;
      drawHeader();
      justStartedPage = true;
    }
  };

  // ── Client info ──────────────────────────────────────────────────────────────
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const clientName = client.spouseName
    ? `${client.name} & ${client.spouseName}`
    : client.name;

  const addrLine1 = client.address || "";
  const addrLine2 = [client.city, client.state, client.zip].filter(Boolean).join(", ");

  const firstName = (client.name || "").split(" ")[0];
  const lastName = (client.name || "").split(" ").slice(1).join(" ");
  const spouseFirst = client.spouseName ? client.spouseName.split(" ")[0] : null;
  const prefix = client.prefix || "";
  const spousePrefix = client.spousePrefix || "";
  let dearName;
  if (spouseFirst) {
    const p1 = prefix || "Mr.";
    const p2 = spousePrefix || "Mrs.";
    dearName = `${p1} ${firstName} & ${p2} ${spouseFirst}`;
  } else if (prefix) {
    dearName = `${prefix} ${lastName || firstName}`;
  } else {
    dearName = `Mr./Ms. ${lastName || firstName}`;
  }

  const scopeLines = (client.scopeOfEngagement || "")
    .split("\n")
    .map(l => l.trim())
    .filter(Boolean);

  const feeRows = (client.feeRows && client.feeRows.length > 0)
    ? client.feeRows.filter(r => r.label || r.amount)
    : (client.fees ? [{ label: "Tax Services", amount: client.fees }] : [{ label: "Tax Services", amount: "$___" }]);

  const businesses = client.businesses || [];

  // Build client name with businesses for intro paragraph
  const businessNames = businesses.map(b => b.legalName).filter(Boolean);
  const clientIntroName = businessNames.length === 0
    ? clientName
    : businessNames.length === 1
      ? `${clientName}, and ${businessNames[0]}`
      : `${clientName}, ${businessNames.slice(0, -1).join(", ")}, and ${businessNames[businessNames.length - 1]}`;

  // ── PAGE 1 ───────────────────────────────────────────────────────────────────
  drawHeader();
  y += 20;

  setFont("normal", 10);
  text(dateStr, marginL, y); y += 28;

  text(clientName, marginL, y); y += 14;
  businessNames.forEach(bName => { text(bName, marginL, y); y += 14; });
  if (addrLine1) { text(addrLine1, marginL, y); y += 14; }
  if (addrLine2) { text(addrLine2, marginL, y); y += 14; }
  y += 14;

  setFont("bold", 10);
  text(`Subject: Engagement Letter - Tax Year ${client.taxYear || "2025"}`, marginL, y); y += 20;

  setFont("normal", 10);
  text(`Dear ${dearName}`, marginL, y); y += 18;

  para(`This Engagement Agreement between ${clientIntroName}  (the "Client") and NetSave Tax & Accounting, LLC, ("NetSave" or "we" or "us") sets forth the terms and conditions of the relationship and the scope of the engagement of NetSave in the provision of services to the Client. Client acknowledges the opportunity to read and consider all provisions of this agreement before signing.`);
  y += 16;

  ensureSpace(20);
  setFont("bolditalic", 10);
  text("SCOPE OF ENGAGEMENT", marginL, y);
  const scopeTitleW = doc.getTextWidth("SCOPE OF ENGAGEMENT");
  doc.setLineWidth(0.6);
  doc.line(marginL, y + 2, marginL + scopeTitleW, y + 2);
  y += 18;

  setFont("normal", 10);
  para("Client has engaged the services of NetSave for the specific purpose of preparing and signing as preparer:");
  y += 8;

  if (scopeLines.length > 0) {
    scopeLines.forEach(line => {
      ensureSpace(14);
      setFont("bold", 10);
      text("o", marginL + 28, y);
      const wrapped = doc.splitTextToSize(line, contentW - 50);
      wrapped.forEach((wl, wi) => text(wl, marginL + 44, y + wi * 14));
      y += wrapped.length * 14 + 2;
    });
  } else {
    ensureSpace(14);
    setFont("bold", 10);
    text("o", marginL + 28, y); text(`${client.taxYear || "2025"} IRS Individual Tax Return`, marginL + 44, y); y += 14;
    ensureSpace(14);
    text("o", marginL + 28, y); text(`${client.taxYear || "2025"} State Income Tax Return`, marginL + 44, y); y += 14;
  }
  y += 6;

  // ── Bookkeeping section — one paragraph + one table for all bookkeeping businesses ──
  const bookkeepingBizList = businesses.filter(b => b.includeBookkeeping);
  if (bookkeepingBizList.length > 0) {
    const bizNameList = bookkeepingBizList.map(b => b.legalName).filter(Boolean);
    const bizNamesStr = bizNameList.length === 1
      ? bizNameList[0]
      : bizNameList.length === 2
        ? `${bizNameList[0]} and ${bizNameList[1]}`
        : `${bizNameList.slice(0, -1).join(", ")}, and ${bizNameList[bizNameList.length - 1]}`;

    setFont("normal", 10);
    const bkText = `Client has also engaged the services of NetSave for the specific purpose of bookkeeping activities for ${bizNamesStr} as described herein. The following table summarizes the specific services we will provide, how frequently we will provide them and the deliverables to be provided to you.`;
    para(bkText);
    y += 6;

    // Single bookkeeping services table
    const colW = [contentW * 0.42, contentW * 0.22, contentW * 0.36];
    const bkServices = [
      { desc: "Assist management in determining appropriate account coding for recurring transactions", freq: "Annually", deliverable: "Chart of Accounts" },
      { desc: "Preparing year-end accounting entries to clean up the balance sheet and income statement for completeness and accuracy.", freq: "Annually", deliverable: "Balance Sheet and Income Statement" },
      { desc: `Reconcile bank account (${bookkeepingBizList[0].bankName || "bank account"}), excluding the inspection of cancelled checks, substitute checks or electronic images of such checks`, freq: "Annually", deliverable: "Bank account reconciliation and reconciling items for client follow-up" },
    ];

    ensureSpace(28);
    const hdrH = 28;
    doc.setFillColor(31, 56, 100);
    doc.rect(marginL, y, contentW, hdrH, "F");
    doc.setTextColor(255, 255, 255);
    setFont("bolditalic", 9);
    const hdrCols = ["Description of Service", "Frequency of Service", "Deliverable"];
    let cx = marginL;
    hdrCols.forEach((h, i) => {
      const lines = doc.splitTextToSize(h, colW[i] - 8);
      const lineH2 = 11;
      const startY = y + (hdrH - lines.length * lineH2) / 2 + lineH2 - 2;
      lines.forEach((l, li) => doc.text(l, cx + 4, startY + li * lineH2));
      cx += colW[i];
    });
    doc.setTextColor(0, 0, 0);
    y += hdrH;

    bkServices.forEach((row, ri) => {
      setFont("normal", 9);
      const cells = [row.desc, row.freq, row.deliverable];
      const cellLines = cells.map((c, ci) => doc.splitTextToSize(c, colW[ci] - 8));
      const maxLines = Math.max(...cellLines.map(l => l.length));
      const rowH = maxLines * 12 + 10;
      ensureSpace(rowH);
      if (ri % 2 === 0) { doc.setFillColor(245, 245, 245); doc.rect(marginL, y, contentW, rowH, "F"); }
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.rect(marginL, y, contentW, rowH, "S");
      let cx2 = marginL;
      cellLines.forEach((lines, ci) => {
        doc.setTextColor(0, 0, 0);
        lines.forEach((l, li) => doc.text(l, cx2 + 4, y + 12 + li * 12));
        cx2 += colW[ci];
        if (ci < 2) { doc.setDrawColor(180, 180, 180); doc.line(cx2, y, cx2, y + rowH); }
      });
      y += rowH;
    });
    // Reset all drawing state after bookkeeping table
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    y += 10;
  }

  setFont("normal", 10);
  para("Client's engagement of NetSave will terminate upon the completion of the matter described herein or upon written notice of termination by either NetSave or the Client."); y += 10;
  para("Any additional accounting, tax, financial advising, investment planning, or consulting services can be provided with an additional signed addendum to this Agreement."); y += 10;
  para('The aforementioned services shall be referred to as the "Services". NetSave shall conduct the Services within the specifications and guidelines set by the Client. NetSave shall, at all times, observe and comply with generally accepted accounting practices and standards while complying with all Federal and State laws, regulations, and procedures when completing their Services in accordance with this Agreement.'); y += 10;
  para("Unless otherwise agreed with NetSave, Client will be responsible for preparation and filing of all other tax or information returns required to be filed with the authorities including, for example, city and county income or gross receipts filings, payroll tax filings, sales and use tax filings, information reporting filings, etc."); y += 10;
  para("Client is responsible for understanding and agreeing with the amounts, computations, and statements made in all of the tax returns before they are filed with the taxing authorities. Most of the tax returns that we will prepare require signatures, under the penalties of perjury, of an appropriate person of Client, as required by the relevant taxing authority, affirming that the tax returns and the accompanying schedules and statements are true, correct, and complete to the best of his or her knowledge."); y += 10;
  para('It is our understanding that you will file the returns as prepared by NetSave unless you inform us otherwise. For those returns that NetSave agrees to be the Electronic Return Originator ("ERO") (see Electronic Filing below), NetSave will file those returns electronically after you have reviewed and approved the returns for filing.');



  // ── PAGE 2 content ────────────────────────────────────────────────────────
  para("Client is required to maintain and retain adequate documentation to support the tax returns as filed as penalties can be imposed by taxing authorities for the failure to produce adequate documentation supporting the items included in a tax return."); y += 10;
  para("We will complete the preparation of the tax returns so they can be timely filed by the applicable due date for such tax returns. We will provide you with draft tax returns for your review. In the event the agreed timetable requires that Client provide us with needed information or assistance within a specified period of time, the failure to timely provide this assistance may require adjustment to our completion date. In addition, in the event unforeseen circumstances occur that impact our ability to meet the final completion date, we will contact Client to discuss an acceptable revised completion date."); y += 14;

  mixedLine([{ txt: "Fees", bold: true }, { txt: ". The Client agrees to pay NetSave: " }]); y += 10;

  // Multi-row fees table
  const feeTableW = contentW;
  const feeColLabel = feeTableW * 0.62;
  const feeColAmt = feeTableW * 0.38;
  ensureSpace(feeRows.length * 30 + 10);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  feeRows.forEach((row, i) => {
    const rowY = y;
    const rowH = 28;
    // Alternate shading
    if (i % 2 === 1) { doc.setFillColor(250, 250, 240); doc.rect(marginL, rowY, feeTableW, rowH, "F"); }
    doc.rect(marginL, rowY, feeTableW, rowH, "S");
    doc.line(marginL + feeColLabel, rowY, marginL + feeColLabel, rowY + rowH);
    setFont("bold", 10);
    doc.setTextColor(0, 0, 0);
    text(row.label || "Service", marginL + 8, rowY + 17);
    // Highlight the amount cell
    doc.setFillColor(255, 255, 150);
    doc.rect(marginL + feeColLabel + 1, rowY + 1, feeColAmt - 2, rowH - 2, "F");
    doc.setTextColor(0, 0, 0);
    setFont("bold", 10);
    text(row.amount || "$___", marginL + feeColLabel + 8, rowY + 17);
    y += rowH;
  });
  // Reset all drawing state after fee table
  doc.setFillColor(255, 255, 255);
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  setFont("normal", 10);
  y += 16;

  ensureSpace(20);
  setFont("bolditalic", 10);
  text("ADDITIONAL TERMS OF ENGAGEMENT", marginL, y);
  const addlW = doc.getTextWidth("ADDITIONAL TERMS OF ENGAGEMENT");
  doc.line(marginL, y + 2, marginL + addlW, y + 2);
  y += 18;

  mixedLine([{ txt: "Payment", bold: true }, { txt: ". Our standard practice is to render our invoices as the services are being performed. Payment of our invoices is due on presentation and expected to be received within 15 days of the invoice date." }]); y += 10;
  mixedLine([{ txt: "Termination", bold: true }, { txt: ". This Agreement shall be terminated by both Parties. By providing the other party at least 14 days' written notice." }]); y += 10;
  para("Unless NetSave has not performed the Services in accordance with this Agreement, the Client shall pay NetSave, in-full, for any remaining balance owed following the termination of Services."); y += 10;
  para("If the Agreement is terminated following tax return preparation, the service is billed in full for the year."); y += 10;
  mixedLine([{ txt: "Client's Obligations", bold: true }, { txt: ". The Client shall be solely responsible for providing NetSave all financial information related to their personal and/or business affairs including, but not limited to, all materials, data, and documents necessary to perform the Services under this Agreement. The Client acknowledges and agrees that the accuracy of financial information supplied to NetSave is the sole responsibility of the Client and NetSave shall be held harmless from any liability resulting from the accuracy of the financial information provided. The Client is also responsible for providing requests in a timely manner. A lack of timely responses results in a termination of the Engagement Agreement." }]); y += 10;
  mixedLine([{ txt: "FinCEN Form 114 (Report of Foreign Bank and Financial Accounts)", bold: true }, { txt: '. Federal law requires that certain individuals and entities report financial interests in, and signatory authority or certain other authority over, foreign financial accounts with more than $10,000 in aggregate value in a calendar year on FinCEN Form 114, Report of Foreign Bank and Financial Accounts. This form is not filed with a tax return. Instead this form must be filed electronically with the U.S. Department of the Treasury through the Financial Crimes Enforcement Network\'s ("FinCEN\'s") BSA E-filing System by April 15 of the year following the calendar year in which aggregate amounts held in the foreign financial accounts meet the threshold. The definition of financial accounts is broadly defined and includes certain interests held indirectly. Failure to comply with these laws could result in significant civil and criminal penalties. Unless specifically listed on the Listing of Tax Returns to be Prepared, NetSave will not prepare, file, or provide assistance with respect to the FinCEN Form 114.' }]);

  // ── PAGE 3 content ────────────────────────────────────────────────────────
  const page3Paras = [
    { bold: "IRS Form 8938", rest: ". Federal law requires that certain individuals and entities include in their income tax returns for any year, a report of all specified foreign financial assets held by them during that year. This reporting, on IRS Form 8938, is required when the taxpayer's specified foreign financial assets exceed a specific threshold during any year. The data gathering, valuation, and associated reporting is complex. Unless specifically listed on the Listing of Forms to be Prepared, Form 8938 is not included in the normal scope of tax services provided under this agreement, and our fee for performing these services will be billed separately based on individual's standard hourly rate." },
    { bold: "Electronic Filing", rest: '. The Internal Revenue Service and some states (collectively, "taxing authorities") offer or require electronic filing for certain tax returns. As part of the services covered by our agreement with you, NetSave may be designated as the Electronic Return Originator ("ERO") with respect to certain returns to be prepared by us. Your designation of NetSave as the ERO allows the taxing authorities to disclose to us the following: 1) any acknowledgement that return(s) have been accepted, 2) the reason(s) for any delay in processing a return or refund, and 3) information regarding any refund offset. For those returns that NetSave agrees to be the ERO, NetSave will file those returns electronically after you have reviewed and approved the returns for filing. For all other returns, it is our understanding that you will file the returns as prepared by NetSave unless you inform us otherwise. If a particular return is ineligible or unable to be processed electronically after making reasonable efforts to do so pursuant to the procedures established by the appropriate taxing authority, NetSave will provide you with a paper return that must be filed by you in accordance with the terms noted in our agreement with you.' },
    { bold: "Employment Status", rest: ". The Parties agree that NetSave shall provide the Services to the Client as an independent contractor and shall not be acting or determined to be an employee, agent, or broker. As an independent contractor, NetSave shall be required to follow all requirements in accordance with the Internal Revenue Code which includes, and is not limited to, payment of all taxes levied for fees collected by the Client for payment of their employees, agents, brokers, and subcontractors. NetSave understands that the Client shall in no way withhold any amounts for payment of any taxes from NetSave's accumulated fees for Services." },
    { bold: "Confidentiality", rest: '. NetSave, shall in the course of performing the Services hereunder, may gain access to certain confidential or proprietary information of the Client. Such "Confidential Information" shall include all information concerning the business, affairs, products, marketing, systems, technology, customers, end-users, financial affairs, accounting, statistical data, documents, discussion, or other information developed by NetSave hereunder and any other proprietary and trade secret information of the Client whether in oral, graphic, electronic or machine-readable form. NetSave agrees to hold all such Confidential Information of the Client in strict confidence and shall not, without the express prior written permission of the client, disclose such Confidential Information to third (3rd) parties or use such Confidential Information for any purposes whatsoever, other than the performance of its obligations hereunder. The obligations under this section shall survive the termination or expiration of this Agreement.' },
    { bold: "Out-of-Scope Work", rest: ". Out-of-scope schedules or tax returns that were not initially discussed or included in this engagement agreement will be billed at the individual's standard hourly rate. You will be notified of any out-of-scope work prior to completion." },
    { bold: "Out-of-Pocket Costs", rest: ". Any additional costs (outside those included in the scope of work) incurred for accounting software fees, registration fees, filing fees, travel (including meals and necessary travel expenses), etc will be billed directly to the Client." },
  ];
  page3Paras.forEach(bp => { mixedLine([{ txt: bp.bold, bold: true }, { txt: bp.rest }]); y += 10; });

  // ── PAGE 4 content ────────────────────────────────────────────────────────
  const page4Paras = [
    { bold: "IRS Notice and Audit Assistance", rest: ". Any assistance with notices or communication from the IRS or state relating to this specific tax return are billed at standard hourly rates." },
    { bold: "Non-Solicitation", rest: ".  During the Period of Agreement and ending two years following the termination of the Period of Agreement, NetSave shall not, and shall not permit any persons or entities subject to NetSave's direction or control to, directly or indirectly, on their own behalf or on behalf of any other person (except the Client or its affiliates), (i) call upon, accept business from, or solicit the business of any person or entity who is, or who had been at any time a customer of the Client, (ii) otherwise divert or attempt to divert any business from the Client, (iii) interfere with the business relationships between the Client and any of its customers, suppliers or others with whom they have business relationships or (iv) recruit or otherwise solicit or induce, or enter into or participate in any plan or arrangement to cause, any person who is an employee of, or otherwise performing services for, the Client to terminate his or her employment or other relationship with the Client, or hire any person who has left the employ of or ceased providing services to the Client during the preceding twelve months." },
    { bold: "Outsourcing", rest: ". NetSave may from time to time, and depending on the circumstances, use third-party service providers in serving your account. NetSave may share confidential information about the Client with these service providers, who shall be required to maintain the confidentiality and security of your information. The Client will be notified prior to any outsourcing to third parties." },
  ];
  page4Paras.forEach(bp => { mixedLine([{ txt: bp.bold, bold: true }, { txt: bp.rest }]); y += 10; });
  para("Accordingly, NetSave maintains internal policies, procedures, and safeguards to protect the confidentiality of Client personal information. In addition, NetSave will secure confidentiality agreements with all service providers to maintain the confidentiality of Client information and Accountant will take reasonable precautions to determine that they have appropriate procedures in place to prevent the unauthorized release of Client confidential information to others."); y += 10;
  para("In the event that NetSave is unable to secure an appropriate confidentiality agreement, the Client will be asked to provide consent prior to the sharing of Client confidential information with the third-party service provider. Furthermore, NetSave will remain responsible for the work provided by any such third-party service providers."); y += 10;
  const page4b = [
    { bold: "Notices", rest: ". Any notices, bills, invoices, or reports required by this Agreement shall be deemed received on the day of delivery if delivered by hand, standard mail, e-mail, or facsimile during the receiving party's regular business hours." },
    { bold: "Governing Law", rest: ". This Agreement shall be construed in accordance with and governed by Federal laws and those located in the State of California." },
    { bold: "Dispute Resolution", rest: ". All disputes under this Agreement shall be settled by arbitration in the State of governing law before a single arbitrator pursuant to the commercial law rules of the American Arbitrator Association. Arbitration may be commenced at any time by any party hereto giving written notice to the other party to a dispute that such dispute has been referred to arbitration. Any award rendered by the arbitrator shall be conclusive and binding upon the parties hereto. This provision for arbitration shall be specifically enforceable by the parties and the decision of the arbitrator in accordance herewith shall be final and binding without right of appeal." },
    { bold: "Severability", rest: ". If any provision of this Agreement shall be held to be illegal, invalid or unenforceable under present or future laws, such provisions shall be severable, this Agreement shall be construed and" },
  ];
  page4b.forEach(bp => { mixedLine([{ txt: bp.bold, bold: true }, { txt: bp.rest }]); y += 10; });

  // ── PAGE 5 content ────────────────────────────────────────────────────────
  para("enforced as if such illegal, invalid or unenforceable provision had never comprised a part of this Agreement; and, the remaining provisions of this Agreement shall remain in full force and effect."); y += 10;
  const page5Paras = [
    { bold: "Limitation of Liability", rest: ". In no event shall either party be liable to the other party for any indirect, incidental, consequential, special or exemplary damages, including without limitation, business interruption, loss of or unauthorized access to information, damages for loss of profits, incurred by the other party arising out of the services provided under this Agreement, even if such party has been advised of the possibility of such damages. In no event will neither party's liability on any claim, loss or liability arising out of or connected with this Agreement shall exceed the amounts paid to NetSave during the period immediately preceding the event giving rise to such claim or action by the Client or the limits of NetSave's professional liability policy, whichever is greater of the errors and omissions policy that is in place." },
    { bold: "Indemnification", rest: ". Each party shall at its own expense indemnify and hold harmless, and at the other party's request defend such party affiliates, subsidiaries, and assigns its respective officers, directors, employees, sublicensees, and agents from and against any and all claims, losses, liabilities, damages, demand, settlements, loss, expenses, and costs, including attorneys' fees and court costs, which arise directly or indirectly out of or related to any breach of this Agreement or the gross negligence or willful misconduct of a party's employees or agents." },
    { bold: "Entire Agreement", rest: ". This Agreement is the final, complete and exclusive agreement of the parties with respect to the subject matter hereof and supersedes and merges all prior or contemporaneous representations, discussions, proposals, negotiations, conditions, communications, and agreements, whether written or oral, between the parties relating to the subject matter hereof and all past courses of dealing or industry custom. No modification of or amendment to this Agreement shall be effective unless in writing and signed by each of the Parties." },
    { bold: "Waiver", rest: ". The waiver by either party of a breach of or a default under any provision of this Agreement shall not be effective unless in writing and shall not be construed as a waiver of any subsequent breach of or default under the same or any other provision of this Agreement, nor shall any delay or omission on the part of either party to exercise or avail itself to any right or remedy that it has or may have hereunder operate as a waiver of any right or remedy." },
    { bold: "Disclosure and Use of Pass-Through Tax Information", rest: ". Client agrees that, if NetSave is engaged to prepare tax filings for an owner of Client, NetSave may disclose and use the respective owner's tax information (e.g., Schedule K-1) to facilitate the preparation of that owner's tax filings. Notwithstanding the foregoing, Client will remain responsible for distributing tax information to its owners." },
  ];
  page5Paras.forEach(bp => { mixedLine([{ txt: bp.bold, bold: true }, { txt: bp.rest }]); y += 10; });
  para("If Client is an owner in an entity whose tax returns have been prepared by NetSave, Client agrees that NetSave may obtain and use tax information from that entity (e.g., Schedule K-1) to facilitate our preparation of your tax returns. Notwithstanding the foregoing, Client will remain responsible for providing all information necessary for preparation of Client's tax return."); y += 10;
  para("The foregoing consents are valid until further notice by Client. Client may request in writing a more limited disclosure than the foregoing."); y += 10;
  mixedLine([{ txt: "Other Matters", bold: true }, { txt: ". No party to this engagement letter may assign or transfer this engagement letter, or any rights, obligations, claims or proceeds from claims arising under it, without the prior written consent of" }]);

  // ── PAGE 6 content ────────────────────────────────────────────────────────
  para("the other party, and any assignment without such consent shall be void and invalid. If any provision of this engagement letter is found to be unenforceable, the remainder of this engagement letter shall be enforced to the extent permitted by law."); y += 10;
  para("If we perform the Services prior to both parties executing this engagement letter or an applicable Statement of Work, this engagement letter or the applicable Statement of Work shall be effective as of the date we began the Services."); y += 10;
  para("Neither party shall be liable to the other for any delay or failure to perform any of the Services or obligations in this engagement letter due to causes beyond its reasonable control. If NetSave is requested or authorized by Client or required by government regulation, regulatory agency, subpoena, or other legal process to produce NetSave's deliverables, working papers or personnel for testimony or interview with respect to services NetSave performed for Client, Client will reimburse NetSave for NetSave's and its counsels' expenses and professional time incurred in responding to such a request."); y += 10;
  para("You agree we may use your name in experience citations and recruiting materials. This engagement letter supersedes any prior understandings, proposals or agreements with respect to the Services, and any changes must be agreed to in writing."); y += 10;
  para("NetSave is owned by professionals who hold CPA licenses as well as by professionals who are not licensed CPAs. Depending on the nature of the Services, non-CPA owners may be involved in providing Services under this engagement letter."); y += 20;

  ensureSpace(20);
  text("* * * *", W / 2, y, { align: "center" }); y += 20;
  para("We are pleased to have the opportunity to provide services to you. If you have any questions about this engagement letter, please discuss them with me at the number below. If you have reviewed this engagement letter in its entirety and the Services and terms outlined in the engagement letter are acceptable to you, please sign and date the engagement letter as provided and return it to the undersigned as requested."); y += 24;

  ensureSpace(80);
  setFont("normal", 10);
  text("Very truly yours,", marginL, y); y += 16;
  text("NetSave Tax & Accounting LLC", marginL, y); y += 32;
  // Dynamic staff name based on assignedTo
  const staffNames = {
    "Biraj":  "Biraj Pandey, Partner",
    "Ben":    "Ben Karki, CPA",
    "Bibek":  "Bibek Kshetri, Partner",
  };
  const staffLabel = staffNames[client.assignedTo] || "Biraj Pandey, Partner";
  const todayStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  text("By:", marginL, y);
  doc.setLineWidth(0.5);
  // Draw staff signature — use saved image or blank line
  const staffSigKey = client.assignedTo || "Biraj";
  const staffSigImg = staffSignatures[staffSigKey];
  if (staffSigImg) {
    try {
      const fmt = staffSigImg.startsWith("data:image/jpeg") ? "JPEG" : "PNG";
      doc.addImage(staffSigImg, fmt, marginL + 24, y - 28, 120, 30);
      doc.line(marginL + 24, y, marginL + 220, y);
    } catch(e) {
      console.warn("Signature image failed:", e.message);
      doc.line(marginL + 24, y, marginL + 220, y);
    }
  } else {
    doc.line(marginL + 24, y, marginL + 220, y);
  }
  y += 16;
  setFont("bold", 10);
  doc.setTextColor(180, 140, 0);
  text(staffLabel, marginL + 24, y);
  doc.setTextColor(0, 0, 0); y += 24;
  setFont("normal", 10);
  text("Date:", marginL, y);
  text(todayStr, marginL + 40, y);
  doc.line(marginL + 36, y, marginL + 220, y); y += 30;

  setFont("bold", 10);
  text("ACKNOWLEDGED AND AGREED:", marginL, y);

  // ── PAGE 7 — Signature Page (always starts on a fresh page) ─────────────────
  forcedPageBreak();
  y += 20;

  setFont("bold", 10);
  text("Signature of Client official:", marginL, y);
  doc.setLineWidth(0.5);
  doc.line(marginL + 180, y, marginL + 380, y);
  // DocuSeal text tag — auto-detected and converted to a signature field
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.text("{{Signature;role=First Party;type=signature}}", marginL + 182, y - 1);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  y += 26;

  text("Name:", marginL, y);
  doc.line(marginL + 50, y, marginL + 280, y);
  setFont("normal", 10);
  text(client.name || "", marginL + 52, y - 2);
  y += 26;

  text("Date:", marginL, y);
  doc.line(marginL + 50, y, marginL + 280, y);
  // DocuSeal date tag
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.text("{{Date;role=First Party;type=date}}", marginL + 52, y - 1);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  y += 40;

  if (client.spouseName) {
    text("Signature of Client's Spouse:", marginL, y);
    doc.line(marginL + 190, y, marginL + 390, y);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text("{{Signature;role=Second Party;type=signature}}", marginL + 192, y - 1);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    y += 26;

    text("Name:", marginL, y);
    doc.line(marginL + 50, y, marginL + 280, y);
    setFont("normal", 10);
    text(client.spouseName || "", marginL + 52, y - 2);
    y += 26;

    text("Date:", marginL, y);
    doc.line(marginL + 50, y, marginL + 280, y);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text("{{Date;role=Second Party;type=date}}", marginL + 52, y - 1);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
  }

  drawFooter();
  return doc;
}

/** Matches the filename the CRM has always used. */
export function engagementFilename(client) {
  const safeName = (client.name || "client").replace(/[^a-z0-9]/gi, "_");
  return `Engagement_Letter_${safeName}_${client.taxYear || "2025"}.pdf`;
}
